import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Snackbar, Stack, Tooltip, Typography } from '@mui/material';
import { Favorite as HeartIcon, ContentCopy as CopyIcon, OpenInNew as OpenIcon, WhatsApp as WhatsAppIcon } from '@mui/icons-material';

// Numero WhatsApp qui recoit les « J'ai fait mon don » (le meme que celui du pied de page)
const CONTACT_WHATSAPP = '237690152052';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// La campagne est lue une fois par page puis gardee 2 minutes : l'interrupteur de l'administration
// agit donc en quelques instants, sans recharger le site.
let cache = { at: 0, value: null, promise: null };
function loadCampaign() {
  if (cache.value && Date.now() - cache.at < 2 * 60 * 1000) return Promise.resolve(cache.value);
  if (!cache.promise) {
    cache.promise = fetch(`${API_URL}/donations/public`)
      .then((r) => (r.ok ? r.json() : { active: false }))
      .catch(() => ({ active: false }))
      .then((v) => { cache = { at: Date.now(), value: v, promise: null }; return v; });
  }
  return cache.promise;
}

const TEXTS = {
  fr: { button: 'Nous soutenir', title: 'Nous soutenir', intro: "Ce site est gratuit. Votre don aide à le faire vivre et à le développer : l'éphéméride, le dictionnaire, le quiz, l'éditeur, et leurs nouveautés (prononciations, nouveaux mots, illustrations).", number: 'Envoyez votre don au numéro', holder: 'Titulaire', copy: 'Copier le numéro', copied: 'Numéro copié', open: 'Faire un don', thanks: 'Merci pour votre soutien !', close: 'Fermer', doneButton: "J'ai fait mon don", doneHint: 'Dites-le-nous sur WhatsApp pour que nous puissions vous remercier.', doneText: "Bonjour, j'ai fait un don pour soutenir l'Éphéméride Duala. Mon nom : ", zoneCm: 'Depuis le Cameroun', zoneIntl: "Depuis l'étranger (Europe, Amériques…)" },
  en: { button: 'Support us', title: 'Support us', intro: "This site is free. Your donation helps keep it alive and growing: the daily ephemeris, the dictionary, the quiz, the editor, and their new features (pronunciations, new words, illustrations).", number: 'Send your donation to the number', holder: 'Account holder', copy: 'Copy the number', copied: 'Number copied', open: 'Donate', thanks: 'Thank you for your support!', close: 'Close', doneButton: 'I have donated', doneHint: 'Tell us on WhatsApp so we can thank you.', doneText: 'Hello, I made a donation to support the Duala Ephemeris. My name: ', zoneCm: 'From Cameroon', zoneIntl: 'From abroad (Europe, Americas…)' },
  es: { button: 'Apóyanos', title: 'Apóyanos', intro: "Este sitio es gratuito. Su donación ayuda a mantenerlo vivo y a desarrollarlo: la efeméride, el diccionario, el quiz, el editor y sus novedades (pronunciaciones, palabras nuevas, ilustraciones).", number: 'Envíe su donación al número', holder: 'Titular', copy: 'Copiar el número', copied: 'Número copiado', open: 'Donar', thanks: '¡Gracias por su apoyo!', close: 'Cerrar', doneButton: 'He donado', doneHint: 'Díganoslo por WhatsApp para que podamos agradecérselo.', doneText: 'Hola, hice una donación para apoyar la Efeméride Duala. Mi nombre: ', zoneCm: 'Desde Camerún', zoneIntl: 'Desde el extranjero (Europa, América…)' },
  de: { button: 'Uns unterstützen', title: 'Uns unterstützen', intro: "Diese Seite ist kostenlos. Ihre Spende hilft, sie am Leben zu halten und weiterzuentwickeln: das Tagesblatt, das Wörterbuch, das Quiz, der Editor und ihre Neuerungen (Aussprachen, neue Wörter, Illustrationen).", number: 'Senden Sie Ihre Spende an die Nummer', holder: 'Kontoinhaber', copy: 'Nummer kopieren', copied: 'Nummer kopiert', open: 'Spenden', thanks: 'Danke für Ihre Unterstützung!', close: 'Schließen', doneButton: 'Ich habe gespendet', doneHint: 'Sagen Sie es uns per WhatsApp, damit wir uns bei Ihnen bedanken können.', doneText: 'Hallo, ich habe für das Duala-Tagesblatt gespendet. Mein Name: ', zoneCm: 'Aus Kamerun', zoneIntl: 'Aus dem Ausland (Europa, Amerika…)' },
};

// Bouton « Soutenir » : n'apparait que pendant une campagne de don ouverte (page « Campagne de don »).
function DonateButton({ language = 'fr', variant = 'outlined', size = 'small', fullWidth = false }) {
  const t = TEXTS[language] || TEXTS.fr;
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState('');
  const [campaign, setCampaign] = useState(null);
  useEffect(() => {
    let alive = true;
    loadCampaign().then((c) => { if (alive) setCampaign(c); });
    return () => { alive = false; };
  }, []);
  if (!campaign || !campaign.active) return null;
  const DONATION_METHODS = campaign.methods || [];
  // Regroupement par zone : « Depuis le Cameroun », « Depuis l'étranger », puis « tous » ; les titres
  // ne s'affichent que s'il y a au moins deux groupes.
  const groups = [['cm', t.zoneCm], ['intl', t.zoneIntl], ['all', '']]
    .map(([zone, title]) => ({ zone, title, methods: DONATION_METHODS.filter((m) => (m.zone || 'all') === zone) }))
    .filter((g) => g.methods.length > 0);
  const showHeadings = groups.length > 1;

  const copy = async (number) => {
    try { await navigator.clipboard.writeText(number.replace(/\s+/g, '')); setNotice(t.copied); }
    catch { setNotice(number); }
  };

  return (
    <>
      <Button variant={variant} size={size} fullWidth={fullWidth} color="error" startIcon={<HeartIcon />} onClick={() => setOpen(true)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 3 }}>
        {t.button}
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800 }}>{t.title}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: campaign.message ? 1 : 2 }}>{t.intro}</Typography>
          {campaign.message && <Typography variant="body2" sx={{ mb: 2, fontWeight: 700 }}>{campaign.message}</Typography>}
          {groups.map((g) => (
            <Box key={g.zone} sx={{ mb: 1.5 }}>
              {showHeadings && g.title && <Typography variant="overline" sx={{ fontWeight: 800, display: 'block', mb: 0.5 }}>{g.title}</Typography>}
              <Stack spacing={1.5}>
                {g.methods.map((m) => (
                <Box key={m.label} sx={{ border: '1px solid', borderColor: 'divider', borderLeft: `6px solid ${m.color || '#999'}`, borderRadius: 2, p: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{m.label}</Typography>
                    {m.type === 'number' ? (
                      <>
                        <Typography variant="caption" color="text.secondary">{t.number}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '0.02em' }}>{m.number}</Typography>
                          <Tooltip title={t.copy}><IconButton size="small" onClick={() => copy(m.number)} aria-label={t.copy}><CopyIcon fontSize="small" /></IconButton></Tooltip>
                        </Box>
                        {m.holder && <Typography variant="caption" color="text.secondary">{t.holder} : {m.holder}</Typography>}
                      </>
                    ) : (
                      <Button component="a" href={m.url} target="_blank" rel="noopener noreferrer" size="small" variant="contained" endIcon={<OpenIcon />} sx={{ mt: 0.5, textTransform: 'none', bgcolor: m.color, '&:hover': { bgcolor: m.color, opacity: 0.9 } }}>
                        {t.open}
                      </Button>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
          ))}
          {DONATION_METHODS.some((m) => m.type === 'number') && (
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{t.doneHint}</Typography>
              <Button component="a" href={`https://wa.me/${CONTACT_WHATSAPP}?text=${encodeURIComponent(t.doneText)}`} target="_blank" rel="noopener noreferrer" size="small" variant="contained" startIcon={<WhatsAppIcon />} sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#25d366', '&:hover': { bgcolor: '#1da851' } }}>
                {t.doneButton}
              </Button>
            </Box>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>{t.thanks}</Typography>
        </DialogContent>
        <DialogActions><Button onClick={() => setOpen(false)}>{t.close}</Button></DialogActions>
      </Dialog>
      <Snackbar open={Boolean(notice)} autoHideDuration={3000} onClose={() => setNotice('')} message={notice} />
    </>
  );
}

export default DonateButton;
